import { type Category, FixedCategory } from "./category";

import type { TemplateVisibility } from "~/__generated__/modalBrowseTemplate.graphql";

export function filterTemplates<
	T extends {
		id?: string;
		tags: readonly string[];
		author: { name?: string; fullName?: string; iconName?: string | null } | null;
		starred: boolean;
		title: string;
		summary: string;
		viewerAppliedAt: string | null;
		visibility: TemplateVisibility;
	},
>(
	noteTemplates: T[],
	category: Category,
	search: string,
	/** Ensure the template with this ID is first, if present. */
	stickyId?: string,
) {
	let templates = [...noteTemplates];
	if (!templates) return [];

	switch (category) {
		case FixedCategory.ALL:
			break;
		case FixedCategory.MY:
			templates = templates.filter(t => t.visibility === "USER");
			break;
		case FixedCategory.WORKSPACE:
			templates = templates.filter(t => t.visibility === "WORKSPACE");
			break;
		case FixedCategory.RECENT:
			// Note that we're bypassing the default sort below. Starred templates
			// don't get bumped to the top, and we're sorting by viewerAppliedAt.
			return templates
				.filter(t => !!t.viewerAppliedAt)
				.sort((a, b) => {
					// typescript pls
					if (!a.viewerAppliedAt || !b.viewerAppliedAt) throw new Error();
					return new Date(b.viewerAppliedAt).valueOf() - new Date(a.viewerAppliedAt).valueOf();
				});
		case FixedCategory.STARRED:
			templates = templates.filter(t => t.starred);
			break;
		default:
			templates = templates.filter(t => t.tags.includes(category));
			break;
	}

	if (search) {
		const lowerSearch = search.toLocaleLowerCase();
		templates = templates.filter(template => {
			const authorName = template.author?.name ?? template.author?.fullName ?? template.author?.iconName ?? "";
			return [template.title, template.summary, authorName]
				.concat(template.tags)
				.some(s => s.toLocaleLowerCase().includes(lowerSearch));
		});
	}
	return templates.sort((a, b) => {
		if (a.id === stickyId && b.id !== stickyId) return -1;
		if (b.id === stickyId && a.id !== stickyId) return 1;
		return 0;
	});
}
